(() => {
  const storage = {
    read(key) { try { const value = JSON.parse(localStorage.getItem(key) || '[]'); return Array.isArray(value) ? value : []; } catch { return []; } },
    toggle(key, id) { const items = this.read(key), next = items.includes(id) ? items.filter(item => item !== id) : [...items, id]; localStorage.setItem(key, JSON.stringify(next)); }
  };
  const saveKey = 'ac-saved-artworks';
  const refreshSaved = root => root.querySelectorAll('[data-ac08-save]').forEach(button => button.setAttribute('aria-pressed', String(storage.read(saveKey).includes(button.dataset.ac08Save))));
  class ScopedElement extends HTMLElement {
    begin() { this.controller?.abort(); this.controller = new AbortController(); return { signal: this.controller.signal }; }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }
  }
  class ArtworkPDP extends ScopedElement {
    connectedCallback() {
      const options = this.begin();
      this.variants = JSON.parse(this.querySelector('[data-ac08-variants]')?.textContent || '[]');
      this.labels = JSON.parse(this.querySelector('[data-ac08-labels]')?.textContent || '{}');
      const initial = this.variants.find(item => String(item.id) === this.querySelector('product-form [name="id"]')?.value);
      if (initial) this.setQuantity(initial.minimum);
      this.decorateButton();
      this.querySelectorAll('[data-ac08-thumb]').forEach(button => button.addEventListener('click', () => this.showMedia(button.dataset.ac08Thumb), options));
      this.querySelector('[data-ac08-variant]')?.addEventListener('change', event => this.selectVariant(event.target.value), options);
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-ac08-save]');
        if (button) this.save(button);
      }, options);
      window.addEventListener('storage', () => refreshSaved(this), options);
      refreshSaved(this);
      this.moveDeliveryEstimate();
      this.deliveryObserver = new MutationObserver(() => this.moveDeliveryEstimate());
      this.deliveryObserver.observe(this.querySelector('.ac08-purchase'), { childList: true, subtree: true });
      document.addEventListener('shopify:section:load', () => this.moveDeliveryEstimate(), options);
    }
    disconnectedCallback() { super.disconnectedCallback(); this.deliveryObserver?.disconnect(); }
    moveDeliveryEstimate() {
      const estimate = this.querySelector('.ot-estimated-shipping');
      const destination = document.querySelector('.ac08-details [data-ac08-delivery-estimate]');
      if (estimate && destination) destination.append(estimate);
    }
    showMedia(id) {
      if (!this.querySelector(`[data-ac08-media="${id}"]`)) return;
      this.querySelectorAll('[data-ac08-media]').forEach(media => {
        media.hidden = media.dataset.ac08Media !== String(id);
        if (media.hidden) media.querySelectorAll('video').forEach(video => video.pause());
      });
      this.querySelectorAll('[data-ac08-thumb]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.ac08Thumb === String(id))));
    }
    selectVariant(id) {
      const variant = this.variants.find(item => String(item.id) === String(id));
      if (!variant) return;
      const form = this.querySelector('product-form form');
      const input = form?.querySelector('[name="id"]');
      if (!input) return;
      input.value = variant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this.setQuantity(variant.minimum);
      this.querySelector('[data-ac08-price]').textContent = variant.price;
      const compare = this.querySelector('[data-ac08-compare]');
      compare.textContent = variant.compare || ''; compare.hidden = !variant.compare;
      const submit = form.querySelector('[type="submit"]');
      submit.disabled = !variant.available;
      submit.textContent = variant.available ? this.labels.add : this.labels.sold;
      this.decorateButton();
      const payment = form.querySelector('.shopify-payment-button');
      if (payment) { payment.hidden = !variant.available; payment.style.display = variant.available ? 'block' : 'none'; }
      if (variant.media) this.showMedia(variant.media);
      const url = new URL(location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url);
    }
    setQuantity(minimum) {
      const form = this.querySelector('product-form form');
      if (!form) return;
      let quantity = form.querySelector('[name="quantity"]');
      if (!quantity) { quantity = document.createElement('input'); quantity.type = 'hidden'; quantity.name = 'quantity'; form.append(quantity); }
      quantity.value = minimum || 1;
    }
    decorateButton() {
      const button = this.querySelector('product-form button[type="submit"]');
      const icon = this.querySelector('[data-ac08-atc-icon]');
      if (button && !button.disabled && icon && !button.querySelector('svg')) button.append(icon.content.cloneNode(true));
    }
    save(button) {
      try { storage.toggle(saveKey, button.dataset.ac08Save); document.querySelectorAll('.ac08').forEach(refreshSaved); }
      catch { this.querySelector('[data-ac08-status]').textContent = 'Saving is unavailable in this browser.'; }
    }
  }
  class ArtworkTabs extends ScopedElement {
    connectedCallback() {
      const options = this.begin();
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      if (!this.tabs.length) return;
      const selected = this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
      this.select(selected);
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(tab), options);
        tab.addEventListener('keydown', event => {
          const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
          if (!keys.includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.select(this.tabs[next]); this.tabs[next].focus();
        }, options);
      });
    }
    select(selected) {
      this.tabs.forEach(tab => {
        const active = tab === selected;
        tab.setAttribute('aria-selected', String(active)); tab.tabIndex = active ? 0 : -1;
        const panel = this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
        if (panel) panel.hidden = !active;
      });
    }
  }
  class ArtworkPhotos extends ScopedElement {
    connectedCallback() {
      const options = this.begin();
      this.photos = [...this.querySelectorAll('[data-ac08-photo]')]; this.index = 0;
      const dots = this.querySelector('.ac08-photo-dots'); dots.replaceChildren();
      this.photos.forEach((photo, index) => {
        const button = document.createElement('button'); button.type = 'button'; button.setAttribute('aria-label', `Artist photo ${index + 1}`);
        button.addEventListener('click', () => this.select(index), options); dots.append(button);
      });
      this.querySelector('[data-ac08-prev]').addEventListener('click', () => this.select(this.index - 1), options);
      this.querySelector('[data-ac08-next]').addEventListener('click', () => this.select(this.index + 1), options);
      this.querySelectorAll('[data-ac08-prev],[data-ac08-next]').forEach(button => button.disabled = this.photos.length < 2);
      this.select(0);
    }
    select(index) {
      this.index = (index + this.photos.length) % this.photos.length;
      this.photos.forEach((photo, i) => photo.hidden = i !== this.index);
      this.querySelectorAll('.ac08-photo-dots button').forEach((button, i) => button.setAttribute('aria-current', String(i === this.index)));
    }
  }
  class ArtworkRail extends ScopedElement {
    connectedCallback() {
      const options = this.begin(); this.track = this.querySelector('.ac08-rail'); this.range = this.querySelector('.ac08-range');
      this.range.addEventListener('input', () => this.track.scrollLeft = (this.track.scrollWidth - this.track.clientWidth) * Number(this.range.value) / 100, options);
      this.track.addEventListener('scroll', () => this.update(), { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft','ArrowRight'].includes(event.key)) return;
        event.preventDefault(); this.track.scrollBy({ left: (event.key === 'ArrowRight' ? 1 : -1) * this.track.clientWidth * .7, behavior: matchMedia('(prefers-reduced-motion:reduce)').matches ? 'instant' : 'smooth' });
      }, options);
      this.observer = new ResizeObserver(() => this.update()); this.observer.observe(this.track); this.update();
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-ac08-save]');
        if (!button) return;
        try { storage.toggle(saveKey, button.dataset.ac08Save); document.querySelectorAll('.ac08').forEach(refreshSaved); }
        catch { button.setAttribute('aria-label', 'Saving is unavailable in this browser'); }
      }, options);
      window.addEventListener('storage', () => refreshSaved(this), options); refreshSaved(this);
    }
    update() { const max = this.track.scrollWidth - this.track.clientWidth; this.range.value = max > 1 ? String(this.track.scrollLeft / max * 100) : '0'; this.range.disabled = max <= 1; }
  }
  class ArtworkArtist extends ScopedElement {
    connectedCallback() {
      const options = this.begin(), button = this.querySelector('[data-ac08-follow]');
      if (!button) return;
      const key = 'ac-followed-artists', id = button.dataset.ac08Follow;
      const refresh = () => { const active = storage.read(key).includes(id); button.setAttribute('aria-pressed',String(active)); button.querySelector('span').textContent = active ? 'Following' : 'Follow'; };
      button.addEventListener('click', () => { try { storage.toggle(key,id); refresh(); } catch { button.querySelector('span').textContent = 'Unavailable'; } }, options);
      window.addEventListener('storage', refresh, options); refresh();
    }
  }
  class ReviewsSlider extends ScopedElement {
    connectedCallback() {
      const options = this.begin();
      this.range = this.querySelector('.ac08-range');
      this.carousel = this.closest('.ac08-review-content')?.querySelector('.jdgm-cards-carousel');
      if (!this.range || !this.carousel) return;
      this.blockId = this.carousel.dataset.jdgmBlockId;
      this.range.addEventListener('input', () => this.jump(), options);
      this.observer = new MutationObserver(() => this.update());
      this.observer.observe(this.carousel, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
      this.update();
    }
    mode() { return window.jdgmCarouselMode?.[this.blockId]; }
    jump() {
      const mode = this.mode();
      if (!mode || mode.revCount < 2) return;
      const target = Math.round(Number(this.range.value) / 100 * (mode.revCount - 1));
      for (let guard = mode.revCount; mode.index !== target && guard--;)
        mode.index > target ? window.jdgmPreviousCard(this.blockId) : window.jdgmNextCard(this.blockId);
    }
    update() {
      const mode = this.mode();
      const usable = !!mode && Number(mode.revCount) > 1;
      this.range.disabled = true;
      this.hidden = !usable;
      if (!usable) { this.range.value = '0'; return; }
      this.range.disabled = false;
      this.range.value = String(Math.round(Math.max(0, Math.min(100, 100 * mode.index / (mode.revCount - 1)))));
    }
  }
  for (const [tag, constructor] of [['ac-artwork-pdp',ArtworkPDP],['ac-artwork-tabs',ArtworkTabs],['ac-artwork-photos',ArtworkPhotos],['ac-artwork-rail',ArtworkRail],['ac-artwork-artist',ArtworkArtist],['ac-reviews-slider',ReviewsSlider]]) {
    if (!customElements.get(tag)) customElements.define(tag, constructor);
  }
})();
